<template>
  <div class="free-response-view">
    <cv-form>
      <question-columns-layout :align-two-columns="alignTwoColumns">
        <template #left>
          <!-- Stimulus -->
          <match-indicator v-if="showStimulusMatchIndicator" />
          <dl v-if="stimulus" class="field-set type-text stimulus">
            <dd class="field-value">
              <rich-text-wrapper
                :content="stimulus"
                :expandable="true"
                :expanded="true"
                :can-annotate="canAccessAnnotations"
                :anchor="stimulusAnchor"
                :resource-type="'LESSON_COMPONENT'"
                :annotation-resource-id="questionAnnotationResourceId"
              />
            </dd>
          </dl>
        </template>
        <template #right>
          <!-- Instructions -->
          <dl
            v-if="isFileSubmissionQuestion || isTeamResponseQuestion"
            class="field-set type-text instructions"
          >
            <dt class="field-label">
              <svg-icon name="Info16" kind="indicative" />
              <span>{{ t("question_component_label_instructions") }}</span>
            </dt>
            <dd v-safe-html="instructionContent" class="field-value"></dd>
          </dl>

          <!-- Question Body -->
          <match-indicator v-if="showTitleMatchIndicator" />
          <dl class="field-set type-text question-body">
            <dd class="field-value">
              <rich-text-wrapper
                :content="questionBody"
                :expandable="true"
                :expanded="true"
                :can-annotate="canAccessAnnotations"
                :anchor="questionBodyAnchor"
                :resource-type="'LESSON_COMPONENT'"
                :annotation-resource-id="questionAnnotationResourceId"
              />
            </dd>
          </dl>
          <!-- Integrator Request Banner -->
          <integrator-request-banner
            v-if="showRequestBanner"
            :app-list="lfaApps"
            :is-in-teacher-marked-quiz="isInTeacherMarkedQuiz"
            :option-selected="optionSelected"
          />

          <!-- Integrated Scorable Response Question -->
          <template v-if="isIsrQuestion">
            <dl class="field-set type-text isr-lti-app-view">
              <dd class="field-value">
                <rich-text
                  :content="isrDefaultAnswer"
                  class="responseSizeHeight"
                  :expandable="false"
                  :expanded="false"
                  truncation-type="secondary"
                  :rich-text-config="defaultAnswerRichTextConfig"
                />
              </dd>
            </dl>
          </template>

          <!-- Default Answer -->
          <dl v-if="!isIsrQuestion" class="field-set type-text default-answer">
            <dd class="field-value">
              <rich-text
                v-if="defaultAnswer"
                :content="defaultAnswer"
                :class="responseSizeHeight"
                :expandable="true"
                :expanded="true"
                :config="defaultAnswerRichTextConfig"
                :show-drawing-restriction="true"
              />
              <text-area
                v-else
                :disabled="true"
                :placeholder="t('question_component_label_view_default_answer_placeholder')"
              />
            </dd>
          </dl>
        </template>
        <template #bottom>
          <!-- Inline Notification below Default Answer -->
          <cv-inline-notification
            v-if="showInlineNotification"
            :title="inlineNotificationContent"
            kind="info"
            low-contrast
            hide-close-button
          />

          <!-- Grading rubric -->
          <dl class="field-set grading-rubrics">
            <slot name="grading-rubric" />
          </dl>

          <cv-inline-notification
            v-if="hasSubmitValidationErrors"
            :class="errorClass"
            :title="submitValidationErrors"
            low-contrast
            kind="error"
          />

          <!-- Suggested Answer(Explanation), Teacher's Feedback -->
          <dl class="field-set type-text answer-info">
            <dd class="field-value">
              <div class="answer-info">
                <div class="answer-info-header">
                  <cv-content-switcher :id="contentSwitcherId" @selected="handleSectionSelected">
                    <content-switcher-button
                      v-for="(header, $index) in sectionHeaders"
                      :key="key($index)"
                      :parent-switcher="contentSwitcherId"
                      :owner-id="key($index)"
                      :selected="isSectionSelected(key($index))"
                    >
                      {{ header }}
                    </content-switcher-button>
                  </cv-content-switcher>
                </div>

                <div class="answer-info-content">
                  <cv-content-switcher-content
                    v-for="(section, $index) in sectionDetails"
                    :key="key($index)"
                    :parent-switcher="contentSwitcherId"
                    :owner-id="key($index)"
                  >
                    <!-- First tab: Suggested Answer -->
                    <rich-text-card
                      v-if="
                        isAlgebraKitSessionIdReady &&
                        section.header === t('question_component_suggested_answer_label')
                      "
                      :title="section.header"
                      :content="section.value"
                      :expandable="true"
                      :visible="isSectionSelected(key($index))"
                      truncation-type="secondary"
                      :rich-text-config="suggestedAnswerRichTextConfig"
                      :anchor="suggestedAnswerAnchor"
                      :resource-type="'LESSON_COMPONENT'"
                      :annotation-resource-id="questionAnnotationResourceId"
                      :can-annotate="canAccessAnnotations"
                    >
                      <template #icon>
                        <svg-icon name="BubbleCheck32" kind="indicative" />
                      </template>
                    </rich-text-card>

                    <!-- Second tab: Teacher Feedback -->
                    <rich-text-card
                      v-if="section.header !== t('question_component_suggested_answer_label')"
                      :title="section.header"
                      :content="section.value"
                      truncation-type="secondary"
                      :visible="isSectionSelected(key($index))"
                    >
                      <template #icon>
                        <svg-icon name="UserFeedback32" kind="indicative" />
                      </template>
                    </rich-text-card>
                  </cv-content-switcher-content>
                </div>
              </div>
            </dd>
          </dl>
        </template>
      </question-columns-layout>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import IntegratorRequestBanner from "@modules/editor/components/IntegratorRequestBanner.vue";
import { useSuggestedAnswerSectionSelection } from "@/modules/editor/composables/suggested-answer-section-selection";
import {
  QUESTION_TYPE,
  QUIZ_TEST_MODE,
  RESPONSE_SIZES_MAPPING,
} from "@modules/editor/service/editor.constant";
import { LFA_CHECKBOX_TYPES, NAMESPACE } from "@modules/attempt/service/attempt.constant";
import { APP_USAGE_TYPE, INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { AlgebraKitEventBus } from "@plugins/richtext/service/algebrakit.event.bus";
import { isEmpty } from "@utils/var.check";
import questionUtils from "@modules/attempt/service/question.utils";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useQuestionView } from "@modules/editor/composables/question-view.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";
import { useLessonSubmissionValidation } from "@modules/editor/composables/lesson-submission-validation.ts";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";
import QuestionColumnsLayout from "@/components/QuestionColumnsLayout.vue";

interface Props {
  question: {
    id?: string | number;
    uuid: string;
    questionType: string;
    questionBody: string;
    stimulus?: string;
    defaultAnswer: string;
    answerValue: string;
    explanation: string;
    answerInputSize: string;
    algebrakitSpec: string;
    algebrakitRefId: string;
    html5Interactive: boolean;
    ltiAppId: null;
    questionAppSettingsModels: {
      appFeatureId: string;
      appUsage: string;
      indexNo: number;
    }[];
  };
  gradingRubricUuid?: string;
  isNew?: boolean;
  mode: string;
  editingRubric?: boolean;
  hasActiveSubQuestion?: boolean;
  highlights?: object | null;
}

const props = withDefaults(defineProps<Props>(), {
  gradingRubricUuid: "",
  isNew: false,
  editingRubric: false,
  hasActiveSubQuestion: false,
  highlights: null,
});

const emit = defineEmits<{
  loaded: [uuid: string];
}>();

const { t } = useI18n();
const { canAccessAnnotations } = useAnnotationPermissions();
const store = useStore();
const { checkIfUnapprovedMOValidationErrorsExist } = useLessonSubmissionValidation();

// Use event bus watch composable
const { trackWatch } = useEventBusWatch();

// Injected dependencies
const resourceContext = inject<{
  quizTestMode: string;
}>("resourceContext");
const getAlgebrakitSpec = inject<() => object | null>("getAlgebrakitSpec");
const getAlgebrakitSessionId = inject<() => string | null>("getAlgebrakitSessionId");

// Reactive data
const sectionSelected = ref(`view_${props.question.uuid}_0_${props.mode.toLowerCase()}`);
const sectionHeaders = ref([
  t("question_component_suggested_answer_label"),
  t("question_component_teacher_feedback_label"),
]);
const submitValidationErrors = ref("");
const hasSubmitValidationErrors = ref(false);
const errorClass = ref("");
const algebraKitSessionId = ref<string | null>(null);
const loadAlgebraKitError = ref(false);
const isLearningFeedbackQuestion = ref(false);
const isAlgebraKitQuestion = ref(false);
const isShortAnswerQuestion = ref(false);
const isGrammarCorrectionQuestion = ref(false);
const isLFAMarked = ref(false);
const lfaApps = ref<object[]>([]);
const algebrakitSpec = ref(
  getAlgebrakitSpec
    ? getAlgebrakitSpec()
    : isEmpty(props.question.algebrakitSpec)
      ? null
      : JSON.parse(props.question.algebrakitSpec),
);
const algebrakitRefId = ref(props.question.algebrakitRefId);

const { questionBodyAnchor, stimulusAnchor, suggestedAnswerAnchor, questionAnnotationResourceId } =
  useQuestionAnnotation();

// Computed properties
const getIntegratorAppAccessByAppFeatureId = computed(
  () => store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`],
);

const isFileSubmissionQuestion = computed(() => props.question.questionType === QUESTION_TYPE.FILE);

const isAlgebraKitQuestionAndComponent = computed(() => {
  const defaultAnswerHtml = document.createElement("div");
  defaultAnswerHtml.innerHTML = props.question.defaultAnswer;
  const hasAlgebraKitComponent = defaultAnswerHtml.querySelector(".sls-algebra-kit") !== null;
  return isAlgebraKitQuestion.value && hasAlgebraKitComponent;
});

const questionBody = computed(() => props.question.questionBody);
const stimulus = computed(() => props.question?.stimulus ?? "");
const defaultAnswer = computed(() => props.question.defaultAnswer);

const isrDefaultAnswer = computed(() => {
  return questionUtils.extractIsrEmbeddedContent(defaultAnswer.value);
});

const sectionDetails = computed(() => [
  {
    header: t("question_component_suggested_answer_label"),
    value: isAlgebraKitQuestionAndComponent.value
      ? props.question.defaultAnswer
      : props.question.answerValue
        ? props.question.answerValue
        : t("question_component_label_view_suggested_answer_placeholder"),
  },
  {
    header: t("question_component_teacher_feedback_label"),
    value: t("question_component_label_teacher_feedback_placeholder"),
  },
]);

const isSectionSelected = computed(() => (section: string) => {
  return section === sectionSelected.value;
});

const contentSwitcherId = computed(
  () => `content_switcher_free_response_view_${props.question.uuid}`,
);

const key = computed(() => (value: number) => {
  return `view_${props.question.uuid}_${value}_${props.mode.toLowerCase()}`;
});

const defaultAnswerRichTextConfig = computed(() => ({
  reviewMode: true,
  solutionMode: false,
  questionUuid: props.question.uuid,
  refId: algebrakitRefId.value,
  spec: algebrakitSpec.value,
  sessionId: algebraKitSessionId.value,
  isScorable: true,
}));

const responseSizeHeight = computed(
  () => RESPONSE_SIZES_MAPPING[props.question.answerInputSize].sizeString,
);

const suggestedAnswerRichTextConfig = computed(() => ({
  reviewMode: false,
  solutionMode: true,
  sessionId: algebraKitSessionId.value,
  loadError: loadAlgebraKitError.value,
  refId: algebrakitRefId.value,
  spec: algebrakitSpec.value,
  isScorable: true,
}));

const inlineNotificationContent = computed(() => t("algebra_kit_show_hints_and_feedback_content"));

const showInlineNotification = computed(() => {
  if (!isEmpty(props.question.defaultAnswer)) {
    let str = props.question.defaultAnswer;
    let result = "";
    str = str.split(/(\s+)/);
    str.forEach((attribute) => {
      if (attribute.includes("data-show-feedback")) {
        result = attribute.substring(attribute.indexOf("=") + 1);
      }
    });
    return result === '"true"';
  }
  return false;
});

const isAlgebraKitSessionIdReady = computed(() => {
  if (!isAlgebraKitQuestionAndComponent.value) {
    return true;
  }
  if (loadAlgebraKitError.value) {
    return true;
  }
  return !isEmpty(algebraKitSessionId.value);
});

const isInTeacherMarkedQuiz = computed(
  () => resourceContext?.quizTestMode === QUIZ_TEST_MODE.TEST_TEACHER_RELEASE,
);

const optionSelected = computed(() => {
  const options = [LFA_CHECKBOX_TYPES.FEEDBACK];
  if (isAlgebraKitQuestionAndComponent.value || isLearningFeedbackQuestion.value) {
    if (isLFAMarked.value) return [...options, LFA_CHECKBOX_TYPES.MARKS];
    return options;
  }
  return [];
});

const showRequestBanner = computed(
  () => isLearningFeedbackQuestion.value || isAlgebraKitQuestionAndComponent.value,
);

const { isTeamResponseQuestion, alignTwoColumns, instructionContent } = useQuestionView({
  question: props.question,
  resourceContext: resourceContext,
  isFileSubmissionQuestion: isFileSubmissionQuestion,
  isShortAnswerQuestion: isShortAnswerQuestion,
});

const showTitleMatchIndicator = computed(() => !isEmpty(props.question.highlights?.title));

const showStimulusMatchIndicator = computed(() => !isEmpty(props.question.highlights?.stimulus));

const suggestedAnswerSectionKey = computed(() => {
  return key.value(0);
});

useSuggestedAnswerSectionSelection({
  sectionSelected,
  suggestedAnswerAnchor,
  suggestedAnswerSectionKey,
});

// Methods
const checkIfSubmitValidationErrorsExist = () => {
  hasSubmitValidationErrors.value = false;
  if (checkIfUnapprovedMOValidationErrorsExist(props.question.uuid)) {
    hasSubmitValidationErrors.value = true;
    submitValidationErrors.value = t("validation_among_tabs_label");
    errorClass.value = "submit-validation-error";
  }
};

const populateIntegratorAppAccess = (integratorAppList: object[]) => {
  integratorAppList.forEach((app: object) => {
    switch ((app as { category: string }).category) {
      case INTEGRATOR_APP_FEATURE_CATEGORY.AK:
        isAlgebraKitQuestion.value = true;
        break;
      case INTEGRATOR_APP_FEATURE_CATEGORY.GC:
      case INTEGRATOR_APP_FEATURE_CATEGORY.AFA:
        isLearningFeedbackQuestion.value = true;
        isGrammarCorrectionQuestion.value = true;
        break;
      case INTEGRATOR_APP_FEATURE_CATEGORY.SA:
        isLearningFeedbackQuestion.value = true;
        isShortAnswerQuestion.value = true;
        break;
      case INTEGRATOR_APP_FEATURE_CATEGORY.HTML5:
        isLearningFeedbackQuestion.value = false;
        break;
      default:
        break;
    }
  });
};

const checkIntegratorAppAccess = () => {
  const questionAppSettings = props.question.questionAppSettingsModels;
  const integratorAppList: object[] = [];
  if (!isEmpty(questionAppSettings)) {
    const appFeatureIds: string[] = [];
    questionAppSettings.forEach((questionAppSettings) => {
      if (appFeatureIds.includes(questionAppSettings.appFeatureId)) {
        return;
      }

      if (questionAppSettings.appUsage === APP_USAGE_TYPE.FEEDBACK_AND_MARKING) {
        isLFAMarked.value = true;
      }

      const appAccess = getIntegratorAppAccessByAppFeatureId.value(
        questionAppSettings.appFeatureId,
      );
      integratorAppList.push({ ...appAccess, indexNo: questionAppSettings.indexNo });
      appFeatureIds.push(questionAppSettings.appFeatureId);
    });
  } else {
    return [];
  }
  lfaApps.value = integratorAppList;
  populateIntegratorAppAccess(integratorAppList);
  return integratorAppList;
};

const handleSectionSelected = (selected: string) => {
  sectionSelected.value = selected;
};

onMounted(() => {
  emit("loaded", props.question.uuid);
  checkIfSubmitValidationErrorsExist();
});

onBeforeMount(() => {
  algebraKitSessionId.value = getAlgebrakitSessionId ? getAlgebrakitSessionId() : null;

  trackWatch(
    AlgebraKitEventBus.$on("createSession", (data: { questionUuid: string; sessionId: string }) => {
      if (props.question.uuid !== data.questionUuid) {
        return;
      }
      algebraKitSessionId.value = data.sessionId;
    }),
  );

  trackWatch(
    AlgebraKitEventBus.$on(
      "loadError",
      (data: { questionUuid: string; sessionId: string; loadError: boolean }) => {
        if (props.question.uuid !== data.questionUuid) {
          return;
        }
        algebraKitSessionId.value = data.sessionId;
        loadAlgebraKitError.value = data.loadError;
      },
    ),
  );

  checkIntegratorAppAccess();
});

const isIsrQuestion = computed(() => {
  return props.question?.ltiAppId != null || props.question?.html5Interactive === true;
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.free-response-view {
  .field-set {
    &.default-answer {
      margin-bottom: 0.75rem;

      .bx--text-area,
      .rich-text {
        padding: $spacing-03 $spacing-05;
        background-color: $ui-01;
        border: 1px solid $ui-03;
      }
    }

    &.answer-info {
      margin-top: $spacing-04;
      padding-top: $spacing-02;
      border-top: $divider-02;

      .rich-text-card {
        border: 1px solid $ui-03;
      }
    }

    &.grading-rubrics {
      .grading-rubric {
        padding-bottom: 0;

        &.empty {
          display: none;
        }
      }
    }
  }
}
</style>
